/**
 * Words for the Quote versions card (SA-1003, SA-1016). Pure: the card hands
 * in what the quotes read served and renders the sentence as text.
 */
import { whenText } from '@/components/process/stepLiveText'
import type { QuoteRefusal, QuoteStep, QuoteVersion } from '@/types/commerce'

function stepName(step: QuoteStep): string {
  return `step ${step.number} ${step.title}`
}

/** "step 12 Offer"; two join with " or ", more with ", " and a last " or ". */
function stepList(steps: QuoteStep[]): string {
  const names = steps.map(stepName)
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

/** "A new version is recorded at step 12 Offer. The process is at step 13
 *  Customer response." */
export function sendRefusalText(refusal: QuoteRefusal): string {
  const current = `The process is at ${stepName(refusal.currentStep)}.`
  if (refusal.waitingSteps.length === 0) return current
  return `A new version is recorded at ${stepList(refusal.waitingSteps)}. ${current}`
}

/** SA-1016: "The customer’s response is recorded at step 13 Customer
 *  response. The process is at step 12 Offer." A process sitting at a step
 *  that waits for the response but no longer listens (a timer that fired
 *  before SA-1016 cleared the wait) is answered on its task instead. */
export function respondRefusalText(refusal: QuoteRefusal): string {
  const current = refusal.currentStep
  if (refusal.waitingSteps.some((s) => s.id === current.id)) {
    return `The process at ${stepName(current)} no longer waits for the response here: complete its task instead.`
  }
  const where = `The process is at ${stepName(current)}.`
  if (refusal.waitingSteps.length === 0) return where
  return `The customer’s response is recorded at ${stepList(refusal.waitingSteps)}. ${where}`
}

/** The record's quote (spec §3.1): the newest version that is not `prepared`; when the only
 *  versions are prepared, the newest of those. The one rule the strip and the closed line read. */
export function recordQuote<T extends Pick<QuoteVersion, 'versionNumber' | 'status'>>(versions: T[]): T | undefined {
  const newest = (list: T[]) => [...list].sort((a, b) => b.versionNumber - a.versionNumber)[0]
  return newest(versions.filter(v => v.status !== 'prepared')) ?? newest(versions)
}

/** The prepare form's visible title. The band's and the Tasks section's Current task block also
 *  render `openTaskAction.RECORD_QUOTE` ("Prepare quote"); the CARD's submit, PREPARE_BUTTON, is the
 *  page's one button named exactly "Prepare". */
export const PREPARE_TITLE = 'Prepare quote'
export const PREPARE_BUTTON = 'Prepare'
/** SA-1040 PR 2 (spec §3.4, §6): why no offer email would be drafted, and what to do instead. The
 *  record's error words (`useRecordExtras.REFUSALS`) carry the same sentences. */
export const DRAFT_REFUSALS: Record<string, string> = {
  no_thread: 'This record has no email thread: download the quotation and send it your own way, then mark it as sent.',
  channel_sends_nothing: 'This record’s thread cannot send email: download the quotation and send it your own way, then mark it as sent.',
  not_authorized: 'This record’s mailbox may not send email (outbound is off, or not consented, on the Mailboxes page): download the quotation and send it your own way, then mark it as sent.',
  no_recipient: 'This record’s thread has no address to reply to: download the quotation and send it your own way, then mark it as sent.',
  send_in_progress: 'A send on this record’s thread has not settled yet: draft the email again once it has.',
}

export function draftRefusalText(code: string): string {
  return DRAFT_REFUSALS[code]
    ?? 'No offer email can be drafted on this record: download the quotation and send it your own way, then mark it as sent.'
}

/** The Prepare form's sentence: what happens after Prepare, by the read's `draftRefusal`. */
export function prepareSentence(draftRefusal: string | null): string {
  if (draftRefusal == null) {
    return 'Preparing renders the quotation on the letterhead and drafts the offer email on this record’s thread, for you to review and send from the Inbox.'
  }
  return `Preparing renders the quotation on the letterhead. ${draftRefusalText(draftRefusal)}`
}

export const OPEN_DRAFT = 'Open the email draft'
export const DRAFT_EMAIL = 'Draft the email'
export const DRAFT_AGAIN = 'Draft the email again'
export const DRAFT_READY = 'The offer email is drafted on the record’s thread.'
export const DRAFT_SENDING = 'The offer email is being sent.'
export const DRAFT_KEEPS_TEXT = 'The email draft keeps the text a person edited: check that it states the new figures before sending.'
/** The mail went out but the version could not be marked after the absorb (the thread carries a staff
 *  note): recording it as emailed is the recovery, never a second email. `at` is `whenText` of the dispatch (null: no time is said). */
export const draftSentUnmarked = (at: string | null): string =>
  `The offer email went out${at ? ` ${at}` : ''}, but this version could not be marked sent. Check the note on the record’s thread, then record it as emailed.`
export const PREPARED_CHIP = 'Prepared · not sent'
export const EDIT = 'Edit'
export const SAVE_EDIT = 'Save changes'
export const CANCEL_EDIT = 'Cancel'
export const DOWNLOAD = 'Download PDF'
export const VIEW = 'View PDF'
export const VIEW_FAILED = 'The document could not be opened.'
export const MARK_SENT = 'Mark as sent another way'
export const MARK_EMAILED = 'Record as emailed'
export const CONFIRM_SENT = 'Confirm sent'
export const NOTE_LABEL = 'Note (optional)'
export const FROM_APPROVAL = 'from the approved price'
export const AMOUNT_LABEL = 'Amount'
export const VALIDITY_LABEL = 'Valid for (hours)'
export const REMARKS_LABEL = 'Remarks'
export const DOWNLOAD_FAILED = 'The document could not be downloaded.'

/** "Sent 15 Oct 14:02 by email" / "Sent 15 Oct 14:02, recorded by hand"; a row from before SA-1040 says only when.
 *  The time is `stepLiveText.whenText`, hand-formatted (never `toLocaleString`), read in `zone`, the deployment's
 *  (`useDefaultTimezone`), so the line says the same thing to every viewer; the year only when it is not `now`'s.
 *  No time leaves it out. */
export function sentLine(v: Pick<QuoteVersion, 'sentAt' | 'sentVia'>, now: Date, zone: string): string {
  const at = whenText(v.sentAt, now, zone)
  const base = `Sent${at ? ` ${at}` : ''}`
  if (v.sentVia === 'email') return `${base} by email`
  if (v.sentVia === 'manual') return `${base}, recorded by hand`
  return base
}

/** "Prepared 15 Oct 13:00 by Maria", in the same zone. */
export function preparedLine(v: Pick<QuoteVersion, 'preparedAt' | 'preparedByName'>, now: Date, zone: string): string {
  const at = whenText(v.preparedAt, now, zone)
  return `Prepared${at ? ` ${at}` : ''}${v.preparedByName ? ` by ${v.preparedByName}` : ''}`
}
